import { writeFetch, getSession } from '../api';

export const TABLE_NAMES = {
  articles: 'platform_articles_1776500001',
  videos:   'help_articles_1776500002',
  featured: 'featured_videos_1776510001',
};

const ARTICLES_TABLE        = TABLE_NAMES.articles;
const VIDEOS_TABLE          = TABLE_NAMES.videos;
const FEATURED_VIDEOS_TABLE = TABLE_NAMES.featured;

// ── Text sanitization ─────────────────────────────────────────────────────────
function _sanitizeText(val) {
  if (val === null || val === undefined) return '';
  return String(val)
    .replace(/\x00/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .trim();
}

function _sanitizeUrl(val) {
  if (!val) return '';
  const s = _sanitizeText(val);
  if (s && !/^https?:\/\//i.test(s)) return '';
  return s;
}

function _safeInt(val, fallback = 0) {
  const n = parseInt(val, 10);
  return Number.isFinite(n) ? n : fallback;
}

function _safeBool(val) {
  if (typeof val === 'boolean') return val ? 1 : 0;
  if (typeof val === 'number')  return val ? 1 : 0;
  return 0;
}

// ── Response parser — handles empty bodies from PUT/DELETE ────────────────────
async function _parseResponse(res, context) {
  const text = await res.text();

  if (!res.ok) {
    let errMsg = text;
    try {
      const errData = JSON.parse(text);
      errMsg = errData.message || errData.error || text;
    } catch { /* use raw text */ }
    console.error(`[NCB] ${context} error (${res.status}):`, errMsg);
    throw new Error(`${context}: ${errMsg || `HTTP ${res.status}`}`);
  }

  // Empty body on PUT/DELETE — treat as success
  if (!text || !text.trim()) {
    return { status: 'success' };
  }

  try {
    const data = JSON.parse(text);
    if (data.status === 'error') {
      throw new Error(`${context}: ${data.message || data.error}`);
    }
    return data;
  } catch (e) {
    if (res.ok) return { status: 'success', raw: text };
    throw new Error(`${context}: server returned unreadable response`);
  }
}

// ── View-count (best-effort, deduplicated per session) ────────────────────────
const _viewedKeys = new Set();
async function _incrementViewCount(table, id, currentCount) {
  const key = `${table}:${id}`;
  if (_viewedKeys.has(key)) return;
  _viewedKeys.add(key);
  try {
    await writeFetch(`update/${table}/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        view_count: (_safeInt(currentCount, 0)) + 1,
        updated_at: new Date().toISOString(),
      }),
    });
  } catch { /* silent */ }
}

// ── Sort-order helper ─────────────────────────────────────────────────────────
export async function updateItemSortOrder(tableKey, id, sort_order) {
  const table = TABLE_NAMES[tableKey];
  const res = await writeFetch(`update/${table}/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      sort_order: _safeInt(sort_order, 999),
      updated_at: new Date().toISOString(),
    }),
  });
  return _parseResponse(res, 'Update sort order');
}

// ── Payload builders ──────────────────────────────────────────────────────────

/**
 * platform_articles_1776500001 columns:
 * owner_id, title, slug, content, excerpt, category, tags,
 * cover_image_url, youtube_url, status, is_ai_generated, ai_provider,
 * ai_prompt, read_time_minutes, view_count, is_featured, published_at,
 * sort_order, created_at, updated_at
 */
function _buildArticleBody(payload, ownerId) {
  const title = _sanitizeText(payload.title);
  // Auto-generate slug from title if not provided
  const slug = _sanitizeText(payload.slug) ||
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return {
    owner_id:          _sanitizeText(ownerId),
    title,
    slug,
    content:           _sanitizeText(payload.content),
    excerpt:           _sanitizeText(payload.excerpt),
    category:          _sanitizeText(payload.category) || 'Articles',
    tags:              _sanitizeText(payload.tags),
    cover_image_url:   _sanitizeUrl(payload.cover_image_url),
    youtube_url:       _sanitizeUrl(payload.youtube_url),
    status:            _sanitizeText(payload.status) || 'published',
    is_ai_generated:   _safeBool(payload.is_ai_generated),
    ai_provider:       _sanitizeText(payload.ai_provider),
    ai_prompt:         _sanitizeText(payload.ai_prompt),
    read_time_minutes: _safeInt(payload.read_time_minutes, 0),
    is_featured:       _safeBool(payload.is_featured),
    published_at:      payload.published_at || new Date().toISOString(),
    sort_order:        _safeInt(payload.sort_order, 999),
    updated_at:        new Date().toISOString(),
  };
}

/**
 * help_articles_1776500002 columns:
 * owner_id, title, content, category, page_context, youtube_url,
 * icon, is_published, sort_order, view_count, is_featured,
 * featured_video_id, created_at, updated_at
 */
function _buildVideoBody(payload, ownerId) {
  const body = {
    owner_id:     _sanitizeText(ownerId),
    title:        _sanitizeText(payload.title),
    content:      _sanitizeText(payload.content),
    category:     _sanitizeText(payload.category) || 'Getting Started',
    page_context: _sanitizeText(payload.page_context),
    youtube_url:  _sanitizeUrl(payload.youtube_url),
    icon:         _sanitizeText(payload.icon) || '🎥',
    is_published: payload.is_published !== undefined ? _safeBool(payload.is_published) : 1,
    is_featured:  _safeBool(payload.is_featured),
    sort_order:   _safeInt(payload.sort_order, 999),
    updated_at:   new Date().toISOString(),
  };

  // Only include featured_video_id if non-empty
  const fvId = payload.featured_video_id ? String(payload.featured_video_id).trim() : '';
  if (fvId) body.featured_video_id = fvId;

  return body;
}

/**
 * featured_videos_1776510001 columns:
 * owner_id, title, content, youtube_url, category, icon,
 * description, is_active, promoted_from_id, sort_order,
 * view_count, created_at, updated_at
 */
function _buildFeaturedBody(payload, userId) {
  const body = {
    owner_id:    _sanitizeText(userId),
    title:       _sanitizeText(payload.title),
    content:     _sanitizeText(payload.content || payload.description || payload.title),
    description: _sanitizeText(payload.description || payload.content || payload.title) || 'Featured video',
    youtube_url: _sanitizeUrl(payload.youtube_url),
    category:    _sanitizeText(payload.category) || 'Getting Started',
    icon:        _sanitizeText(payload.icon) || '⭐',
    is_active:   payload.is_active !== undefined ? _safeBool(payload.is_active) : 1,
    sort_order:  _safeInt(payload.sort_order, 10),
    updated_at:  new Date().toISOString(),
  };

  const pfId = payload.promoted_from_id ? String(payload.promoted_from_id).trim() : '';
  if (pfId) body.promoted_from_id = pfId;

  return body;
}

// ── Articles ──────────────────────────────────────────────────────────────────
export async function fetchArticles() {
  const res = await writeFetch(`read/${ARTICLES_TABLE}?sort=sort_order&order=asc&limit=200`);
  const data = await _parseResponse(res, 'Fetch articles');
  return Array.isArray(data.data) ? data.data : [];
}

export async function incrementArticleView(id, currentCount) {
  return _incrementViewCount(ARTICLES_TABLE, id, currentCount);
}

export async function createArticle(payload) {
  const user = getSession() || { id: 'admin' };
  const body = {
    ..._buildArticleBody(payload, user.id),
    view_count: 0,
    created_at: new Date().toISOString(),
  };

  console.log('[NCB] createArticle payload:', JSON.stringify(body, null, 2));

  const res = await writeFetch(`create/${ARTICLES_TABLE}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return _parseResponse(res, 'Create article');
}

export async function updateArticle(id, payload) {
  const user = getSession() || { id: 'admin' };
  const body = _buildArticleBody(payload, user.id);

  console.log('[NCB] updateArticle', id, 'payload:', JSON.stringify(body, null, 2));

  const res = await writeFetch(`update/${ARTICLES_TABLE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return _parseResponse(res, 'Update article');
}

export async function deleteArticle(id) {
  const res = await writeFetch(`delete/${ARTICLES_TABLE}/${id}`, { method: 'DELETE' });
  return _parseResponse(res, 'Delete article');
}

// ── Videos (help_articles) ────────────────────────────────────────────────────
export async function fetchHelpArticles() {
  const res = await writeFetch(`read/${VIDEOS_TABLE}?sort=sort_order&order=asc&limit=200`);
  const data = await _parseResponse(res, 'Fetch videos');
  return Array.isArray(data.data) ? data.data : [];
}

export async function incrementVideoView(id, currentCount) {
  return _incrementViewCount(VIDEOS_TABLE, id, currentCount);
}

export async function createHelpArticle(payload) {
  const user = getSession() || { id: 'admin' };
  const body = {
    ..._buildVideoBody(payload, user.id),
    view_count: 0,
    created_at: new Date().toISOString(),
  };

  console.log('[NCB] createHelpArticle payload:', JSON.stringify(body, null, 2));

  const res = await writeFetch(`create/${VIDEOS_TABLE}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return _parseResponse(res, 'Create video');
}

export async function updateHelpArticle(id, payload) {
  const user = getSession() || { id: 'admin' };
  const body = _buildVideoBody(payload, user.id);

  console.log('[NCB] updateHelpArticle', id, 'payload:', JSON.stringify(body, null, 2));

  const res = await writeFetch(`update/${VIDEOS_TABLE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return _parseResponse(res, 'Update video');
}

export async function deleteHelpArticle(id) {
  const res = await writeFetch(`delete/${VIDEOS_TABLE}/${id}`, { method: 'DELETE' });
  return _parseResponse(res, 'Delete video');
}

// ── Featured Videos ───────────────────────────────────────────────────────────
export async function fetchFeaturedVideos() {
  const res = await writeFetch(`read/${FEATURED_VIDEOS_TABLE}?sort=sort_order&order=asc&limit=200`);
  const data = await _parseResponse(res, 'Fetch featured videos');
  return Array.isArray(data.data) ? data.data : [];
}

export async function incrementFeaturedVideoView(id, currentCount) {
  return _incrementViewCount(FEATURED_VIDEOS_TABLE, id, currentCount);
}

export async function createFeaturedVideo(payload) {
  const user = getSession() || { id: 'admin' };
  const body = {
    ..._buildFeaturedBody(payload, user.id),
    view_count: 0,
    created_at: new Date().toISOString(),
  };

  console.log('[NCB] createFeaturedVideo payload:', JSON.stringify(body, null, 2));

  const res  = await writeFetch(`create/${FEATURED_VIDEOS_TABLE}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await _parseResponse(res, 'Create featured video');

  // Back-link: mark source video as featured
  if (payload.promoted_from_id && data.id) {
    try {
      await writeFetch(`update/${VIDEOS_TABLE}/${payload.promoted_from_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          is_featured:       1,
          featured_video_id: String(data.id),
          updated_at:        new Date().toISOString(),
        }),
      });
    } catch (e) {
      console.warn('[NCB] Back-link update failed (non-fatal):', e.message);
    }
  }

  return data;
}

export async function updateFeaturedVideo(id, payload) {
  const user = getSession() || { id: 'admin' };
  const body = _buildFeaturedBody(payload, user.id);

  const res  = await writeFetch(`update/${FEATURED_VIDEOS_TABLE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  const data = await _parseResponse(res, 'Update featured video');

  // Keep back-link in sync
  if (payload.promoted_from_id && String(payload.promoted_from_id).trim() !== '') {
    try {
      await writeFetch(`update/${VIDEOS_TABLE}/${payload.promoted_from_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          is_featured:       1,
          featured_video_id: String(id),
          updated_at:        new Date().toISOString(),
        }),
      });
    } catch (e) {
      console.warn('[NCB] Back-link update failed (non-fatal):', e.message);
    }
  }

  return data;
}

export async function deleteFeaturedVideo(id) {
  const res = await writeFetch(`delete/${FEATURED_VIDEOS_TABLE}/${id}`, { method: 'DELETE' });
  return _parseResponse(res, 'Delete featured video');
}